/* eslint-disable max-lines */
import gql from 'graphql-tag';

import { CatchFieldsForPostsViewer } from './fragments/CatchFieldsForPostsViewer';
import { CountryFragments } from './fragments/CountryType';
import { FishingWaterReviewFragments } from './fragments/FishingWaterReviewType';
import { FishingWaterFragments } from './fragments/FishingWaterType';
import { ImageFragments } from './fragments/ImageType';
import { PlaceFragments } from './fragments/PlaceType';
import { PostFragments } from './fragments/PostType';
import { RegionFragments } from './fragments/RegionType';
import { SpeciesFragments } from './fragments/SpeciesTypes';
import { UserFragments } from './fragments/UserType';
import { CatchFragments } from './fragments/CatchType';

/* Fishing water in a map drawer */
export const getFishingWaterSummary = gql`
  query GetFishingWaterSummary($externalId: String, $first: Int, $after: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      ...FishingWaterLocationFields
      displayName
      metadata {
        id
        description
        name
        variant
        group {
          id
        }
      }
      catchesCount
      allReviews: reviews {
        totalCount
        totalAverage(attribute: RATING)
      }
      catches(first: $first, after: $after, filters: { withPhotos: true, orderByRecent: true }) {
        totalCount
        pageInfo {
          startCursor
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...CatchFieldsForPostsViewer
          }
        }
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
  ${CatchFieldsForPostsViewer}
`;

/* Fishing water page, review section */
export const getFishingWaterReviews = gql`
  query GetFishingWaterReviews(
    $first: Int
    $after: String
    $externalId: String
    $orderByTopReviews: Boolean
    $orderByRecent: Boolean
  ) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      oneStarReviews: reviews(filters: { havingOneStarRating: true }) {
        totalCount
      }
      twoStarReviews: reviews(filters: { havingTwoStarRating: true }) {
        totalCount
      }
      threeStarReviews: reviews(filters: { havingThreeStarRating: true }) {
        totalCount
      }
      fourStarReviews: reviews(filters: { havingFourStarRating: true }) {
        totalCount
      }
      fiveStarReviews: reviews(filters: { havingFiveStarRating: true }) {
        totalCount
      }
      reviews(
        first: $first
        after: $after
        filters: {
          havingTextOrTitle: true
          orderByTopReviews: $orderByTopReviews
          orderByRecent: $orderByRecent
        }
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...FishingWaterReviewId
            externalId
            rating
            createdAt
            author {
              ...UserId
              nickname
              lastName
              firstName
            }
            post {
              ...PostId
              helpfulDownvotesCount
              helpfulUpvotesCount
              currentUserFoundHelpful
              titleString
              text {
                text
              }
            }
          }
        }
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterReviewFragments.id}
  ${PostFragments.id}
  ${UserFragments.id}
`;

/* Fishing water page, topspecies section */
export const getFishingWaterTopSpecies = gql`
  query GetFishingWaterTopSpecies($externalId: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      topSpeciesSummary(first: 3) {
        edges {
          node {
            ...SpeciesId
            anglers: catches(filters: { fishingWaterId: $externalId }) {
              topAnglers {
                totalCount
              }
            }
            displayName
            image {
              ...ImageFields
            }
          }
        }
      }
    }
  }
  ${FishingWaterFragments.id}
  ${ImageFragments.imageFields}
  ${SpeciesFragments.id}
`;

/* Fishing water page, nearby places section */
export const getFishingWaterNearestPlace = gql`
  query GetFishingWaterNearestPlace($externalId: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      nearestPlace {
        ...PlaceId
        latitude
        longitude
        name
        slug
        country {
          ...CountryId
        }
        region {
          ...RegionId
          regionCode
        }
        nearbyPlaces(first: 23) {
          edges {
            distanceUnit
            node {
              _id: externalId
              name
              slug
              latitude
              longitude
              country {
                ...CountryId
              }
              region {
                ...RegionId
              }
            }
          }
        }
      }
    }
  }
  ${FishingWaterFragments.id}
  ${CountryFragments.id}
  ${RegionFragments.id}
  ${PlaceFragments.id}
`;

/* Fishing water page, nearby waters  */
export const getFishingWaterNearbyWaters = gql`
  query GetFishingWaterNearbyWaters($externalId: String, $recentCatchLimitDays: Int) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      suggestedWaters {
        ...FishingWaterId
        ...FishingWaterLocationFields
        displayName
        catchesCount
        newCatchCount: catches(filters: { lastNDays: $recentCatchLimitDays }) {
          totalCount
        }
        topSpeciesSummary(first: 3) {
          edges {
            node {
              displayName
            }
          }
        }
        metadata {
          id
          description
          group {
            id
          }
          name
          variant
        }
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
`;

/* Fishing water page, metadata generation for SEO */
export const getFishingWaterSEOMetadata = gql`
  query GetFishingWaterSEOMetadata($externalId: String) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      ...FishingWaterLocationFields
      displayName
      nearestPlace {
        ...PlaceId
        name
      }
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
  ${PlaceFragments.id}
`;

/* Fishing water page, main query */
export const getFishingWater = gql`
  query GetFishingWater($externalId: String, $recentCatchLimitDays: Int) {
    fishingWater(externalId: $externalId) {
      ...FishingWaterId
      ...FishingWaterLocationFieldsExtended
      followedByCurrentUser
      displayName
      metadata {
        id
        description
        group {
          id
        }
        name
        variant
      }
      catchesCount
      newCatchCount: catches(filters: { lastNDays: $recentCatchLimitDays }) {
        totalCount
      }
      topAnglers: catches(filters: { withPhotos: true }) {
        anglers: topAnglers(first: 3) {
          totalCount
          edges {
            node {
              ...UserId
              followedByCurrentUser
              displayIcon(height: 256, width: 256) {
                width: width
                height: height
                url
              }
              displayName
              nickname
            }
          }
        }
      }
      catches(first: 6, filters: { withPhotos: true, orderByCaughtAt: true }) {
        edges {
          node {
            ...CatchId
            post {
              ...PostId
              catch {
                ...CatchId
                ...CatchFishingWaterName
                ...CatchSpeciesName
                caughtAtGmt
                weight
                length
              }
              createdAt
              displayProductUnits {
                totalCount
              }
              images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
                totalCount
                edges {
                  node {
                    ...ImageFields
                  }
                }
              }
              user {
                ...UserId
                nickname
              }
            }
          }
        }
      }
      descriptionOfWaterType
      allReviews: reviews {
        totalCount
        totalAverage(attribute: RATING)
      }
      reviews(first: 6, filters: { havingTextOrTitle: true, orderByHelpfulVotes: true }) {
        amenities: propertyQuestions(filters: { highlighted: true }, first: 20) {
          __typename
          edges {
            __typename
            node {
              __typename
              summaryHeader
            }
          }
        }
      }
      url
    }
  }
  ${CatchFragments.id}
  ${CatchFragments.fishingWaterName}
  ${CatchFragments.speciesName}
  ${CountryFragments.id}
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFieldsExtended}
  ${ImageFragments.imageFields}
  ${PostFragments.id}
  ${RegionFragments.id}
  ${UserFragments.id}
`;

/* Used in making a post when you add a location */
export const getFishingWaterForCard = gql`
  query GetFishingWaterForCard($externalId: String) {
    entry: fishingWater(externalId: $externalId) {
      ...FishingWaterId
      ...FishingWaterLocationFields
      displayName
    }
  }
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
`;

/* Used for redirecting */
export const getFishingWaterName = gql`
  query GetFishingWaterName($externalId: String) {
    entry: fishingWater(externalId: $externalId) {
      ...FishingWaterId
      displayName
    }
  }
  ${FishingWaterFragments.id}
`;

export const getFishingWaterNames = gql`
  query GetFishingWaterNames($externalIds: [String!], $first: Int, $filters: FishingWaterFilters) {
    entries: fishingWaters(first: $first, externalIds: $externalIds, filters: $filters) {
      edges {
        node {
          ...FishingWaterId
          displayName
        }
      }
    }
  }
  ${FishingWaterFragments.id}
`;

export const getNearbyFishingWaters = gql`
  query GetNearbyFishingWaters($first: Int, $boundingBox: BoundingBoxInputObject) {
    entry: fishingWaters(
      first: $first
      filters: { orderByCatchesCount: true, withCatches: true, publicOnly: true }
      boundingBox: $boundingBox
    ) {
      edges {
        node {
          ...FishingWaterId
          ...FishingWaterLocationFields
          catchesCount
          displayName
          allReviews: reviews {
            totalCount
            totalAverage(attribute: RATING)
          }
          recentCatches: catches(first: 4, filters: { withPhotos: true, orderByRecent: true }) {
            edges {
              node {
                ...CatchId
                post {
                  ...PostId
                  images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
                    totalCount
                    edges {
                      node {
                        ...ImageFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${PostFragments.id}
  ${ImageFragments.imageFields}
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
`;

/* Used in suggest water page */
export const getFishingWaterMetadata = gql`
  query GetFishingWaterMetadata($cursor: String) {
    fishingWaterMetadata(first: 100, after: $cursor) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        node {
          __typename
          id
          name
          group {
            id
            name
            choice
          }
        }
      }
    }
  }
`;

/* Used for top waters info */
export const getTopWatersInfo = gql`
  query GetTopWatersInfo($externalIds: [String!], $first: Int) {
    fishingWaters(
      first: $first
      externalIds: $externalIds
      filters: { orderByCatchesCount: true }
    ) {
      edges {
        node {
          externalId
          catchesCount
          displayName
          descriptionOfWaterType
          region {
            ...RegionId
            name
          }
        }
      }
      totalCount
    }
  }
  ${RegionFragments.id}
`;
